import { FC } from "react";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
    DatePicker,
    formatPhone,
    formatSsn,
    formatZip,
    MaskedPhoneInputControlled,
    MaskedSsnInputControlled,
    MaskedZipCodeInputControlled,
    Shimmer,
    SingleSelectControlled,
    useToast,
} from "@am-tax/fe-core";
import { CandidateStatus, EntityCandidateForUpdate } from "../../api/models/EntityCandidate";
import { getCandidateStatusTitle } from "../../components/CandidateStatus";
import { StateResponse, useStates } from "../../api";
import { useCandidatePatch } from "../../api/hooks/useCandidatePatch";
import { buildJsonPatch } from "../../utils/buildJsonPatch";
import { EmailInputControlled } from "@am-tax/wotc-common";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    candidate: Partial<EntityCandidateForUpdate>;
    cId: string | undefined;
    eId: string | undefined;
    candidateId: string | undefined;
}

type FormValues = Partial<EntityCandidateForUpdate>;

export const CandidateGeneralModal: FC<Props> = ({ isOpen, onClose, candidate, cId, eId, candidateId }) => {
    const toast = useToast();
    const statesQuery = useStates();
    const patchCandidate = useCandidatePatch();

    const formValues = useMemo(
        () => ({
            ...candidate,
            phone: formatPhone(candidate.phone),
            socialSecurityNumber: formatSsn(candidate.socialSecurityNumber),
            zipCode: formatZip(candidate.zipCode),
        }),
        [candidate]
    );

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, dirtyFields },
        reset,
    } = useForm<FormValues>({ values: formValues as FormValues });

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit: SubmitHandler<FormValues> = async data => {
        const ops = buildJsonPatch(dirtyFields as Record<string, unknown>, data as Record<string, unknown>);
        if (ops.length > 0) {
            await patchCandidate.mutateAsync({ clientId: cId, entityId: eId, candidateId, patch: ops });
        }
        toast({ title: "Saved", status: "success", duration: 3000, isClosable: true });
        handleClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size={"2xl"}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Edit General Info</ModalHeader>
                <ModalCloseButton />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <fieldset disabled={patchCandidate.isLoading}>
                            <VStack gap={"1rem"} alignItems={"start"}>
                                {/* Name */}
                                <Flex gap={"1rem"} width={"100%"} alignItems={"end"}>
                                    <FormControl isInvalid={!!errors?.firstName}>
                                        <FormLabel>First Name:</FormLabel>
                                        <Input placeholder="First" {...register("firstName", { required: "First Name is required" })} />
                                        <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl>
                                        <Input placeholder="Middle" {...register("middleName")} />
                                    </FormControl>
                                </Flex>
                                <HStack width={"100%"} gap={"1rem"}>
                                    <FormControl isInvalid={!!errors?.lastName}>
                                        <Input placeholder="Last" {...register("lastName", { required: "Last Name is required" })} />
                                        <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl>
                                        <Input placeholder="Suffix" {...register("suffix")} />
                                    </FormControl>
                                </HStack>

                                {/* Status */}
                                <FormControl isInvalid={!!errors?.status}>
                                    <FormLabel>Status:</FormLabel>
                                    <Select placeholder={"Select a Status"} {...register("status", { required: "Status is required." })}>
                                        {Object.values(CandidateStatus).map(s => (
                                            <option key={s} value={s}>
                                                {getCandidateStatusTitle(s)}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>{errors?.status?.message}</FormErrorMessage>
                                </FormControl>

                                {/* Address */}
                                <FormControl>
                                    <FormLabel>Street 1:</FormLabel>
                                    <Input placeholder="1 Main Street" {...register("street1")} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Street 2:</FormLabel>
                                    <Input placeholder="Apt / Suite" {...register("street2")} />
                                </FormControl>
                                <HStack width={"100%"} gap={"1rem"}>
                                    <FormControl>
                                        <FormLabel>City:</FormLabel>
                                        <Input placeholder="City" {...register("city")} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>County:</FormLabel>
                                        <Input placeholder="County" {...register("county")} />
                                    </FormControl>
                                </HStack>
                                <HStack width={"100%"} gap={"1rem"}>
                                    <FormControl>
                                        <FormLabel>Zip Code:</FormLabel>
                                        <MaskedZipCodeInputControlled control={control} name="zipCode" invalidMessage={"Zip code is invalid"} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>State:</FormLabel>
                                        <Shimmer isLoading={statesQuery.isLoading}>
                                            <SingleSelectControlled
                                                control={control}
                                                name="state"
                                                placeholder={"State"}
                                                options={statesQuery.data?.map((s: StateResponse) => ({ label: s.name, value: s.code }))}
                                            />
                                        </Shimmer>
                                    </FormControl>
                                </HStack>

                                {/* Personal Info */}
                                <FormControl>
                                    <FormLabel>Phone:</FormLabel>
                                    <MaskedPhoneInputControlled control={control} name="phone" invalidMessage={"Phone Number is invalid"} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Email:</FormLabel>
                                    <EmailInputControlled control={control} name="email" invalidMessage={"Email is invalid"} placeholder={"me@email.com"} />
                                </FormControl>
                                <FormControl isInvalid={!!errors?.socialSecurityNumber}>
                                    <FormLabel>Social Security Number:</FormLabel>
                                    <MaskedSsnInputControlled
                                        control={control}
                                        name="socialSecurityNumber"
                                        rules={{ required: "Social Security Number is required" }}
                                        invalidMessage={"Social Security Number is invalid"}
                                    />
                                    <FormErrorMessage>{errors?.socialSecurityNumber?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Date of Birth:</FormLabel>
                                    <DatePicker {...register("dateOfBirth")} />
                                </FormControl>
                            </VStack>
                        </fieldset>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            type="submit"
                            variant={"primary"}
                            mr={3}
                            isLoading={patchCandidate.isLoading}
                            isDisabled={patchCandidate.isLoading || Object.keys(errors).length > 0}
                            loadingText={"Saving"}
                        >
                            Save
                        </Button>
                        <Button variant="ghost" onClick={handleClose}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};
